// Fase 10 (10-14, D-13, D-25): la copia fija de las propuestas del plan. Las etiquetas de los botones
// siguen el lienzo de cada pantalla (D8: «Aprobar · Editar · Rechazar»; E5, que monta 10-18:
// «Aprobar · Rechazar · Editar antes de aprobar»); la acción y el endpoint son los mismos.
import type { TipoPropuestaPlan } from '@/types/planTerapeutico';

export type VarianteDecision = 'seccion' | 'trabajo';
export type AccionDecision = 'aprobar' | 'editar' | 'rechazar';

export interface EtiquetasDecision {
    readonly orden: readonly AccionDecision[];
    readonly aprobar: string;
    readonly editar: string;
    readonly rechazar: string;
}

const ORDEN_SECCION: readonly AccionDecision[] = ['aprobar', 'editar', 'rechazar'];
const ORDEN_TRABAJO: readonly AccionDecision[] = ['aprobar', 'rechazar', 'editar'];

export const ETIQUETAS_DECISION: Readonly<Record<VarianteDecision, EtiquetasDecision>> = Object.freeze({
    seccion: Object.freeze({ orden: ORDEN_SECCION, aprobar: 'Aprobar', editar: 'Editar', rechazar: 'Rechazar' }),
    trabajo: Object.freeze({ orden: ORDEN_TRABAJO, aprobar: 'Aprobar', editar: 'Editar antes de aprobar', rechazar: 'Rechazar' }),
});

// D8-16: la etiqueta del bloque cambia con lo que se propone.
export const ETIQUETA_BLOQUE_POR_TIPO: Readonly<Record<TipoPropuestaPlan, string>> = Object.freeze({
    plan_progreso_objetivo: 'Avance propuesto por el asistente · pendiente de tu aprobación',
    plan_cambio_indicador: 'Avance propuesto por el asistente · pendiente de tu aprobación',
    plan_nueva_tarea: 'Tarea propuesta por el asistente · pendiente de tu aprobación',
    plan_nuevo_objetivo: 'Objetivo propuesto por el asistente · pendiente de tu aprobación',
    plan_datos_generales: 'Enfoque y revisión propuestos',
});

// E5-16 (lienzo E_Plan): la misma etiqueta sin el «tu» de D8: «… · pendiente de aprobación».
const ETIQUETA_BLOQUE_TRABAJO: Readonly<Record<TipoPropuestaPlan, string>> = Object.freeze({
    plan_progreso_objetivo: 'Avance propuesto por el asistente · pendiente de aprobación',
    plan_cambio_indicador: 'Avance propuesto por el asistente · pendiente de aprobación',
    plan_nueva_tarea: 'Tarea propuesta por el asistente · pendiente de aprobación',
    plan_nuevo_objetivo: 'Objetivo propuesto por el asistente · pendiente de aprobación',
    plan_datos_generales: 'Enfoque y revisión propuestos',
});

export function etiquetaBloque(tipo: TipoPropuestaPlan, variante: VarianteDecision): string {
    return (variante === 'trabajo' ? ETIQUETA_BLOQUE_TRABAJO : ETIQUETA_BLOQUE_POR_TIPO)[tipo];
}

export const TEXTOS_PLAN = Object.freeze({
    aprobada: 'Cambio aprobado por ti',
    tareaAprobada: 'Tarea aprobada por ti',
    rechazadaPor: 'Propuesta del asistente · rechazada por ti',
    recuperar: 'Recuperar propuesta',
    diagnosticoAnterior: 'Basada en un diagnóstico anterior',
    regenerar: 'Regenerar',
    tomadaDelPlanAnterior: 'Tomado de tu plan anterior',
    chipPlanAnterior: 'Plan anterior',
    // D-46: el rótulo de todo lo que el terapeuta dictó en el chat (línea del panel, pastilla del bloque, historial).
    dictadaPorTi: 'Dictada por ti',
    aprobarConCambios: 'Aprobar con cambios',
    cancelar: 'Cancelar',
    tituloDatosGenerales: 'Enfoque y revisión propuestos',
});

// E5-46: lo que anuncia la región `aria-live` del panel, solo tras confirmarlo el servidor.
export const ANUNCIO_APROBADO = 'Cambio aprobado';
export const ANUNCIO_RECHAZADO = 'Propuesta rechazada';
export const ANUNCIO_RECUPERADO = 'Propuesta recuperada';
export const ANUNCIO_REGENERANDO = 'Regenerando la propuesta';

// D-02, E5-42, E5-43: los avisos en línea de un fallo de decisión.
export const AVISO_PLAN_DESACTUALIZADO = 'El plan ha cambiado desde esta propuesta. Revísala de nuevo';
export const AVISO_YA_DECIDIDA = 'Esta propuesta ya se ha decidido.';
export const AVISO_ERROR_DECISION = 'No se ha podido guardar la decisión. Inténtalo de nuevo.';
// WR-20: el servidor rechazó los datos editados; repetir el envío no los arregla.
export const AVISO_DATOS_FINALES_INVALIDOS = 'Algún valor no es válido para el plan. Revisa los campos y vuelve a intentarlo.';
export const AVISO_ERROR_RECUPERAR = 'No se ha podido recuperar la propuesta. Inténtalo de nuevo.';
export const AVISO_NO_RECUPERABLE = 'Esta propuesta ya no se puede recuperar. Revisa el plan.';
export const AVISO_ERROR_REGENERAR = 'No se ha podido regenerar la propuesta. Inténtalo de nuevo.';
export const AVISO_NO_REGENERABLE = 'Esta propuesta no se puede regenerar.';
export const AVISO_GENERACION_EN_CURSO = 'Ya se está generando una propuesta.';

// 10-15 (D-25): la columna de tareas. La etiqueta es la alternativa neutra de E5-22 porque la ficha del
// paciente no guarda el sexo y esta fase no puede añadir un campo nuevo para ello.
export const TEXTOS_TAREAS = Object.freeze({
    titulo: 'Tareas entre sesiones',
    nueva: '+ Tarea',
    pie: 'Todo cambio del asistente queda en «Historial de cambios» con quién lo aprobó y cuándo.',
    // E5-26 (lienzo E_Plan): el botón de crear se llama «+ Añadir tarea» y la leyenda ocupa el lugar del pie.
    nuevaTrabajo: '+ Añadir tarea',
});

// 10-15: los avisos en línea de la gestión manual del plan.
export const AVISO_ERROR_DIA = 'No se ha podido guardar el día. Inténtalo de nuevo.';
export const AVISO_DIA_FUERA_DE_VENTANA = 'Ese día ya no se puede registrar.';
export const AVISO_ELEMENTO_YA_NO_EXISTE = 'Este elemento ya no está en el plan.';
export const AVISO_PLAN_CAMBIADO = 'El plan ha cambiado. Vuelve a abrir el formulario';
export const AVISO_RETIRADA_DESACTUALIZADA = 'El plan ha cambiado. Revisa el elemento y vuelve a intentarlo.';
export const AVISO_ERROR_GUARDAR = 'No se ha podido guardar. Inténtalo de nuevo.';
export const AVISO_ERROR_RETIRAR = 'No se ha podido retirar. Inténtalo de nuevo.';
export const MENSAJE_PROGRESO_FUERA_DE_RANGO = 'El progreso debe ser un número entero entre 0 y 100';
export const MENSAJE_VECES_FUERA_DE_RANGO = 'Las veces por semana deben ser un número entero entre 1 y 7';

// D8-27: la confirmación de retirar. Lo retirado sale de la lista pero queda en el historial.
export const TEXTOS_RETIRADA = Object.freeze({
    retirar: 'Retirar',
    retirando: 'Retirando…',
    tituloObjetivo: 'Retirar objetivo',
    tituloTarea: 'Retirar tarea',
    descripcionObjetivo: (nombre: string) =>
        `«${nombre}» saldrá de la lista y quedará en el historial de cambios. Sus tareas seguirán en el plan, pero quedarán sin objetivo vinculado.`,
    descripcionTarea: (nombre: string) => `«${nombre}» saldrá de la lista de tareas y quedará en el historial de cambios.`,
});

// 10-16 (D8b, D8-31..D8-33, D8-38, D-17, D-20): el estado vacío del plan y el bloque del asistente. Sin
// nombre de pila en el contexto del tablero el título usa la forma neutra: nunca se inventa uno.
export const TEXTOS_VACIO = Object.freeze({
    sinPlan: 'Sin plan todavía',
    titulo: (nombre: string | null) => `${nombre ?? 'Este paciente'} aún no tiene plan de tratamiento`,
    explicacion:
        'Define el enfoque, los objetivos con su indicador (base, actual y meta) y las tareas para casa. Puedes empezar tú o pedir al asistente una propuesta.',
    tituloPedir: 'Pedir una propuesta de plan al asistente',
    tituloConvertir: 'Convertir tu plan anterior en objetivos y tareas',
    promesa: 'Llegará como propuesta: cada objetivo y cada tarea con Aprobar / Editar / Rechazar. Nada se guarda sin tu aprobación.',
    pedir: 'Pedir propuesta',
    convertir: 'Convertir',
    preparando: 'Preparando la propuesta…',
    // D8-32, E5-50: sin diagnóstico del terapeuta el asistente no propone.
    sinDiagnostico: 'Registra tu diagnóstico para que el asistente pueda proponerte un plan',
    registrarDiagnostico: 'Registrar diagnóstico',
    // D8-33: nunca se inventa un plan sin material.
    sinMaterial: 'Aún no hay sesiones ni documentos con los que proponer un plan',
    // 12.1: «sin material» con sesiones ya procesadas: son anteriores a v1.8 y no tienen citas extraídas.
    sinMaterialSesionesAnteriores:
        'No hay citas de sesiones recientes en las que basar una propuesta. Las sesiones anteriores a esta versión no tienen citas extraídas; la propuesta estará disponible a partir de la próxima sesión.',
    fallo: 'No se pudo preparar la propuesta. Inténtalo de nuevo',
    // El servidor no ha dado por buena ninguna propuesta: se dice, en vez de dejar el panel como estaba.
    sinPropuestas: 'El asistente no ha podido proponer nada esta vez. Puedes empezar tú con «+ Objetivo».',
    conversionPendiente: 'Ya hay propuestas de tu plan anterior pendientes de revisar',
    planAnterior: 'Tu plan anterior (texto libre)',
});

// E5-37, E5-50 (10-18): el estado vacío del panel de trabajo, con los rótulos de E5 en lugar de los de D8b.
export const TEXTOS_VACIO_TRABAJO = Object.freeze({
    titulo: 'Este paciente aún no tiene objetivos en su plan',
    anadirObjetivo: 'Añadir objetivo',
    pedir: 'Proponer objetivos a partir de las sesiones',
    promesa: 'Llegará como propuesta: cada objetivo y cada tarea con Aprobar / Rechazar / Editar antes de aprobar. Nada se guarda sin tu aprobación.',
    planAnterior: 'Plan anterior (texto libre)',
    sinDiagnostico: 'Registra tu diagnóstico para que el asistente pueda sugerir avances',
    sinPropuestas: 'El asistente no ha podido proponer nada esta vez. Puedes empezar tú con «Añadir objetivo».',
});

export const TEXTOS_GESTION = Object.freeze({
    nuevoObjetivo: '+ Objetivo',
    nuevaTarea: '+ Tarea',
    historial: 'Historial de cambios',
    editarEnfoque: 'Editar enfoque y revisión',
});
